import React, { useCallback, useEffect, useRef, useState } from 'react';
import { trainingReportApi } from '../../services/trainingReportApi';
import './TrainingReportPreview.css';

export const TrainingReportPreviewStep = ({
  idReporte,
  accessToken,
  report,
  onPrevious,
  onNext,
}) => {
  const objectUrlRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);

  const clearPreviewUrl = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setPreviewUrl('');
  }, []);

  const loadPreview = useCallback(async () => {
    if (!accessToken) return;

    setLoading(true);
    setStatus({ type: 'info', message: 'Generando vista previa PDF...' });

    try {
      const blob = await trainingReportApi.preview(idReporte, accessToken);
      clearPreviewUrl();
      const url = URL.createObjectURL(blob);
      objectUrlRef.current = url;
      setPreviewUrl(url);
      setStatus({
        type: 'success',
        message: 'Vista previa generada. Este PDF aún no es el documento definitivo.',
      });
    } catch (error) {
      console.error('No se pudo generar la vista previa:', error);
      clearPreviewUrl();
      setStatus({
        type: 'error',
        message: error?.message || 'No fue posible generar la vista previa PDF.',
      });
    } finally {
      setLoading(false);
    }
  }, [accessToken, clearPreviewUrl, idReporte]);

  useEffect(() => {
    loadPreview();
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [loadPreview]);

  const openInNewTab = () => {
    if (!previewUrl) return;
    window.open(previewUrl, '_blank', 'noopener,noreferrer');
  };

  const statusClass =
    status?.type === 'success'
      ? 'admin-operation-status-success'
      : status?.type === 'error'
        ? 'admin-operation-status-error'
        : 'admin-operation-status-warning';

  return (
    <>
      <div className="admin-operation-panel-heading">
        <div>
          <p className="admin-operation-eyebrow">Paso 6 de 7</p>
          <h2>Vista previa PDF</h2>
          <p>
            Revisa el documento tal como se generará. La vista previa incluye la
            marca “VISTA PREVIA” y no cambia el estado del reporte.
          </p>
        </div>
        <span className={`training-report-status training-report-status-${report?.estado || 'borrador'}`}>
          {report?.estado === 'generado' ? 'Generado' : 'Borrador'}
        </span>
      </div>

      <div className="training-report-preview-toolbar">
        <div>
          <strong>Documento de revisión</strong>
          <span>
            Verifica datos generales, asistencia, resultados, firma y Anexo 1 antes
            de generar el PDF definitivo.
          </span>
        </div>
        <div className="training-report-preview-actions">
          <button
            className="admin-operation-button admin-operation-button-secondary"
            type="button"
            onClick={loadPreview}
            disabled={loading}
          >
            {loading ? 'Generando...' : 'Actualizar vista previa'}
          </button>
          <button
            className="admin-operation-button admin-operation-button-secondary"
            type="button"
            onClick={openInNewTab}
            disabled={!previewUrl || loading}
          >
            Abrir en nueva pestaña
          </button>
        </div>
      </div>

      {status && (
        <div className={`admin-operation-status ${statusClass}`} role="status">
          {status.message}
        </div>
      )}

      <div className="training-report-preview-frame-wrapper">
        {loading && !previewUrl ? (
          <div className="training-report-preview-placeholder">
            <strong>Preparando PDF...</strong>
            <span>El documento se mostrará aquí en cuanto termine de renderizarse.</span>
          </div>
        ) : previewUrl ? (
          <iframe
            className="training-report-preview-frame"
            src={previewUrl}
            title={`Vista previa del reporte ${idReporte}`}
          />
        ) : (
          <div className="training-report-preview-placeholder training-report-preview-placeholder-error">
            <strong>Vista previa no disponible</strong>
            <span>Corrige el error mostrado arriba y vuelve a intentarlo.</span>
          </div>
        )}
      </div>

      <div className="admin-operation-status admin-operation-status-warning training-report-preview-warning">
        Este PDF es solo una vista previa. El reporte seguirá en estado borrador hasta
        completar el Paso 7: Generar.
      </div>

      <div className="training-report-editor-actions">
        <button
          className="admin-operation-button admin-operation-button-secondary"
          type="button"
          onClick={onPrevious}
          disabled={loading}
        >
          Volver a revisión
        </button>
        <button
          className="admin-operation-button"
          type="button"
          onClick={onNext}
          disabled={loading || !previewUrl}
        >
          Continuar a generar
        </button>
      </div>
    </>
  );
};
